import { useCallback, useState } from "react";
import { useColetaPrecosListaPagination } from "../hooks/ColetaPrecos_lista/pagination";
import { ColetaPrecoLista } from "../../domain/models/coleta_preco_lista";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { ProdutoServicoDropDown } from "../../../../common/domain/models/produto_servico_dropdown";
import { RequisicaoCompraDropDown } from "../../../../common/domain/models/requisicao_compra_dropdown";
import { initialColetaPrecosListaFilters, useColetaPrecosListaFilters } from "../hooks/ColetaPrecos_lista/filters";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { OrdemCompraDropDown } from "../../../../common/domain/models/ordem_compra_dropdown";
import { TelasIds } from "../../../../common/utils/tela_ids";

/**
 * LoadColetaPrecosFuncState
 */

type LoadColetaPrecosFornecedoresFuncState = {
  dataRequisicaoStart?: Date | null;
  dataRequisicaoEnd?: Date | null;
  fornecedor?: number[] | null;
  requisicao?: number[] | null;
  produtoServico?: number[] | null;
  centroCusto?: number[] | null;
  nroColeta?: string | null;
  ordemCompra?: number[] | null;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useColetaPrecosListaViewModel
 */

export const useColetaPrecosListaViewModel = () => {
  const {
    compras: {
      getAllColetaPrecoUseCase,
      copyColetaPrecoUseCase,
      deleteColetaPrecoUseCase,
    },
    reports: {
      getReportColetaPrecoUseCase,
    }
  } = useDependencyInjection();

  const {
    loadFornecedoresDropDown,
    loadCentroCustoDropDown,
    loadProdutoServicosDropDown,
    loadOrdemComprasDropDown,
    loadRequisicaoCompraDropDown,
  } = useDropDownLoader();

  const {
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  } = usePageLoader();

  const debouncedHandleRef = useCreateDebounceHandler(750);

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [coletaPrecos, setColetaPrecosFornecedores] = useState<ColetaPrecoLista[]>();

  const [selectedColetaPreco, setSelectedColetaPreco] = useState<ColetaPrecoLista>();

  const [fornecedoresDropdown, setFornecedoresDropdown] = useState<FornecedorDropDown[]>();
  const [requisicoesCompraDropdown, setRequisicoesCompraDropdown] = useState<RequisicaoCompraDropDown[]>();
  const [produtoServicosDropdown, setProdutoServicosDropdown] = useState<ProdutoServicoDropDown[]>();
  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [ordemComprasDropdown, setOrdemComprasDropdown] = useState<OrdemCompraDropDown[]>();

  const [coletaPrecoDeleteModal, setColetaPrecoDeleteModal] = useState(false);
  const [legendalModal, setLegendalModal] = useState(false);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filters = useColetaPrecosListaFilters();
  const pagination = useColetaPrecosListaPagination();

  /**
   * Funcoes de carregamento
   */

  const loadColetaPrecoFornecedores = useCallback(async (fs?: LoadColetaPrecosFornecedoresFuncState) => {
    try {
      const filteredDataRequisicaoStart = evaluateParamValue(fs?.dataRequisicaoStart, filters.dataRequisicaoStart);
      const filteredDataRequisicaoEnd = evaluateParamValue(fs?.dataRequisicaoEnd, filters.dataRequisicaoEnd);
      const filteredFornecedor = evaluateParamValue(fs?.fornecedor, filters.fornecedor);
      const filteredRequisicao = evaluateParamValue(fs?.requisicao, filters.requisicao);
      const filteredProdutoServico = evaluateParamValue(fs?.produtoServico, filters.produtoServico);
      const filteredCentroCusto = evaluateParamValue(fs?.centroCusto, filters.centroCusto);
      const filteredNroColeta = evaluateParamValue(fs?.nroColeta, filters.nroColeta);
      const filteredOrdemCompra = evaluateParamValue(fs?.ordemCompra, filters.ordemCompra);
      const filteredStatus = fs?.status ?? filters.status;

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllColetaPrecoUseCase.getAllColetaPreco(
        filteredDataRequisicaoStart,
        filteredDataRequisicaoEnd,
        filteredFornecedor,
        filteredRequisicao,
        filteredProdutoServico,
        filteredCentroCusto,
        filteredNroColeta,
        filteredOrdemCompra,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setColetaPrecosFornecedores(getAllColetaPrecoUseCase.items);
      pagination.setTotalItems(getAllColetaPrecoUseCase.totalItems);
    } catch (e: unknown) {
      setColetaPrecosFornecedores(undefined);
      pagination.setTotalItems(0);
      throw new Error('Falha ao buscar coletas de preços!');
    }
  }, [
    filters,
    pagination,
    getAllColetaPrecoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadProdutoServicosDropDown(dt => setProdutoServicosDropdown(dt), {});

      await loadOrdemComprasDropDown(dt => setOrdemComprasDropdown(dt.map(x => ({ idOrdemCompra: x.id, descricao: x.title }))));

      await loadRequisicaoCompraDropDown(dt => setRequisicoesCompraDropdown(dt));

      await loadColetaPrecoFornecedores();

    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadProdutoServicosDropDown(dt => setProdutoServicosDropdown(dt), {});

      await loadOrdemComprasDropDown(dt => setOrdemComprasDropdown(dt.map(x => ({ idOrdemCompra: x.id, descricao: x.title }))));

      await loadRequisicaoCompraDropDown(dt => setRequisicoesCompraDropdown(dt));

      await loadColetaPrecoFornecedores();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFornecedoresDropDown,
    loadCentroCustoDropDown,
    loadProdutoServicosDropDown,
    loadOrdemComprasDropDown,
    loadRequisicaoCompraDropDown,
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleColetaPreco = useCallback((idColetaPreco: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      if (idColetaPreco) {
        window.open(`/ColetaPrecos_cadastro?id=${idColetaPreco}`, '_blank', 'noopener,noreferrer');
      } else {
        window.open(`/ColetaPrecos_cadastro`, '_blank', 'noopener,noreferrer');
      }
    } else {
      if (idColetaPreco) {
        backOrNavigate(`/ColetaPrecos_cadastro?id=${idColetaPreco}`);
      } else {
        backOrNavigate(`/ColetaPrecos_cadastro`);
      }
    }
  }, [
    backOrNavigate,
  ]);

  const handleColetaPrecoCopy = useCallback(async (idColetaPreco: number) => {
    if (window.confirm('Deseja realmente Copiar este item?')) {
      try {
        setSaving(true);
        setPageSaving(true);
        await copyColetaPrecoUseCase.copyColetaPreco(idColetaPreco);
        await loadColetaPrecoFornecedores();
        pushInfoNotification('Sucesso', 'Registro copiado com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao copiar coleta de preço!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    copyColetaPrecoUseCase,
    loadColetaPrecoFornecedores,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleColetaPrecoDeleteSubmit = useCallback(async () => {
    if (selectedColetaPreco) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteColetaPrecoUseCase.deleteColetaPreco(TelasIds.ColetaPrecos_lista, selectedColetaPreco.idColetaPreco);

        await loadColetaPrecoFornecedores();

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');

        setColetaPrecoDeleteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao copiar coleta de preço!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedColetaPreco,
    deleteColetaPrecoUseCase,
    loadColetaPrecoFornecedores,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleColetaPrecoExportar = useCallback(async (idColetaPreco: number) => {
    try {
      setSaving(true);
      setPageSaving(true);
      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportColetaPrecoUseCase.getReportColetaPreco(
            requiredMimeType,
            idColetaPreco,
          ),
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatorio de coleta de preço!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    getReportColetaPrecoUseCase,
    startReportViewerProcess,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleShowLegenda = useCallback((show: boolean) => {
    setLegendalModal(show);
  }, [
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useColetaPrecosListaPagination.setState({ page: page + 1 });
      await loadColetaPrecoFornecedores({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useColetaPrecosListaPagination.setState({ page: 1, rowsPerPage });
      await loadColetaPrecoFornecedores({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleDataRequisicaoChange = useDebouncedCallback(debouncedHandleRef, (de: Date | null, ate: Date | null) => {
    useColetaPrecosListaFilters.setState({ dataRequisicaoStart: de, dataRequisicaoEnd: ate });
    return async () => {
      try {
        setLoading(true);
        useColetaPrecosListaPagination.setState({ page: 1 });
        await loadColetaPrecoFornecedores({ dataRequisicaoStart: de, dataRequisicaoEnd: ate, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleFornecedorChange = useCallback(async (fornecedor: number[] | null) => {
    try {
      setLoading(true);
      useColetaPrecosListaFilters.setState({ fornecedor });
      useColetaPrecosListaPagination.setState({ page: 1 });
      await loadColetaPrecoFornecedores({ fornecedor, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleRequisicaoChange = useCallback(async (requisicao: number[] | null) => {
    try {
      setLoading(true);
      useColetaPrecosListaFilters.setState({ requisicao });
      useColetaPrecosListaPagination.setState({ page: 1 });
      await loadColetaPrecoFornecedores({ requisicao, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleProdutoServicoChange = useCallback(async (produtoServico: number[] | null) => {
    try {
      setLoading(true);
      useColetaPrecosListaFilters.setState({ produtoServico });
      useColetaPrecosListaPagination.setState({ page: 1 });
      await loadColetaPrecoFornecedores({ produtoServico, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleCentroCustoChange = useCallback(async (centroCusto: number[] | null) => {
    try {
      setLoading(true);
      useColetaPrecosListaFilters.setState({ centroCusto });
      useColetaPrecosListaPagination.setState({ page: 1 });
      await loadColetaPrecoFornecedores({ centroCusto, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleNroColetaChange = useDebouncedCallback(debouncedHandleRef, async (nroColeta: string | null) => {
    useColetaPrecosListaFilters.setState({ nroColeta });
    return async () => {
      try {
        setLoading(true);
        useColetaPrecosListaPagination.setState({ page: 1 });
        await loadColetaPrecoFornecedores({ nroColeta, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleOrdemCompraChange = useCallback(async (ordemCompra: number[] | null) => {
    try {
      setLoading(true);
      useColetaPrecosListaFilters.setState({ ordemCompra });
      useColetaPrecosListaPagination.setState({ page: 1 });
      await loadColetaPrecoFornecedores({ ordemCompra, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (status: number) => {
    try {
      setLoading(true);
      useColetaPrecosListaFilters.setState({ status });
      useColetaPrecosListaPagination.setState({ page: 1 });
      await loadColetaPrecoFornecedores({ status, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useColetaPrecosListaFilters.setState({ ...initialColetaPrecosListaFilters });
      useColetaPrecosListaPagination.setState({ page: 1 });
      await loadColetaPrecoFornecedores({ ...initialColetaPrecosListaFilters, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadColetaPrecoFornecedores,
    pushErrorNotification,
  ]);

  const handleColetaPrecoModalOpenState = useCallback((idColetaPreco: number | null, openState: boolean) => {
    if (openState) {
      if (idColetaPreco && coletaPrecos) {
        setSelectedColetaPreco(coletaPrecos.find(x => x.idColetaPreco === idColetaPreco));
      } else {
        setSelectedColetaPreco(undefined);
      }
    }
    setColetaPrecoDeleteModal(openState);
  }, [
    coletaPrecos,
  ]);

  const handleColetaPrecoOrdemCompra = useCallback((idColetaPreco: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/OrdemCompras_cadastro?id=${idColetaPreco}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/OrdemCompras_cadastro?id=${idColetaPreco}`);
    }
  }, [
    backOrNavigate,
  ]);



  return {
    loading,
    saving,
    errorMessage,
    coletaPrecos: coletaPrecos ?? [],
    selectedColetaPreco: selectedColetaPreco ?? null,
    fornecedoresDropdown: fornecedoresDropdown ?? [],
    requisicoesCompraDropdown: requisicoesCompraDropdown ?? [],
    produtoServicosDropdown: produtoServicosDropdown ?? [],
    centroCustoDropdown: centroCustoDropdown ?? [],
    ordemComprasDropdown: ordemComprasDropdown ?? [],
    coletaPrecoDeleteModal,
    legendalModal,
    reportViewerState,
    filters,
    pagination,
    handleReload,
    handleColetaPreco,
    handleColetaPrecoCopy,
    handleColetaPrecoDeleteSubmit,
    handleColetaPrecoExportar,
    handleShowLegenda,
    handlePageChange,
    handleRowsPerPageChange,
    handleDataRequisicaoChange,
    handleFornecedorChange,
    handleRequisicaoChange,
    handleProdutoServicoChange,
    handleCentroCustoChange,
    handleNroColetaChange,
    handleOrdemCompraChange,
    handleStatusChange,
    handleClearFilters,
    handleColetaPrecoModalOpenState,
    handleColetaPrecoOrdemCompra,
  };
}